import clsx from "clsx";
import React from "react";
import makeStyles from "@mui/styles/makeStyles";
import { Box, Typography, SxProps, Theme, Collapse, Fade } from "@mui/material";

import {
  ExerciseSetNumberType,
  ExerciseTypeExtra,
  ExerciseTypeSet,
  Units,
} from "../../../constants";
import { useWorkoutTableStyles } from "../../workout/tableStyles";
import { hasExtraColumn, isPositiveNumber } from "../../workout/utils";
import HistorySetNumberTypeMarking from "./HistorySetNumberTypeMarking";
import { unitSetLabelCoach } from "../../../utils/units";
import { DiffBadge } from "./DiffBadge";
import { NormalizedSet } from "../../client-workouts/workoutUtils";

const useStyles = makeStyles((theme) => {
  const bodyStyles = {
    fontSize: 18,
    color: theme.palette.text.primary,
    [theme.breakpoints.down("md")]: {
      fontSize: 14,
    },
  };

  return {
    body: {
      display: "flex",
      maxHeight: 42,
      gap: 20,
      alignItems: "center",
      borderRadius: 4,
      marginBottom: 4,
    },
    setItem: {
      display: "flex",
      alignItems: "baseline",
      gap: theme.spacing(1),
    },
    bodySet: {
      fontSize: 16,
      color: theme.palette.text.secondary,
      [theme.breakpoints.down("md")]: {
        fontSize: 14,
      },
    },
    bodyExtraColumn: bodyStyles,
    bodyReps: bodyStyles,
    bodyWeight: bodyStyles,
    bodySettings: {
      marginRight: 20,
      borderRadius: theme.spacing(0.5),
      height: theme.spacing(5),
    },
    fieldContainer: {
      display: "flex",
      justifyContent: "center",
      "& input[type=number]": {
        "-moz-appearance": "textfield",
      },

      "& input::placeholder": {
        color: theme.palette.text.disabled,
      },

      "& input:focus::placeholder": {
        color: "transparent",
      },

      "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
        {
          "-webkit-appearance": "none",
          margin: 0,
        },
    },
  };
});
const displayValue = (v: any): number | string => {
  return isPositiveNumber(v) ? v : 0;
};
export interface HistorySetClasses {
  root?: string;
  unitText?: string;
}

type WorkoutHistorySetProps = {
  set: NormalizedSet;
  setNumber: number;
  typeExtraMeasurement: ExerciseTypeExtra;
  exerciseTypeSet: ExerciseTypeSet;
  units: Units;
  className?: string;
  classes?: HistorySetClasses;
  circleClassName?: string;
  sx?: SxProps<Theme>;
  showDiff?: boolean;
};

const WorkoutHistorySet = ({
  set,
  setNumber,
  typeExtraMeasurement,
  exerciseTypeSet,
  units,
  classes,
  circleClassName,
  sx,
  showDiff = false,
}: WorkoutHistorySetProps) => {
  const s = useStyles();
  const tableStyles = useWorkoutTableStyles();

  if (!set) return null;

  const renderDiffBadge = (diff: number | null) => {
    return (
      <Fade
        in={showDiff && diff !== null && diff !== 0}
        timeout={300}
        unmountOnExit
      >
        <Box display="inline-flex">
          <DiffBadge value={diff!} show={showDiff} />
        </Box>
      </Fade>
    );
  };

  return (
    <Box
      sx={sx}
      className={clsx(
        s.body,
        classes?.root,
        hasExtraColumn(typeExtraMeasurement) && tableStyles.rowWithExtraColumn,
      )}
    >
      <div>
        <HistorySetNumberTypeMarking
          setNumber={setNumber + 1}
          type={set.type ?? ExerciseSetNumberType.REGULAR_SET}
          className={circleClassName}
        />
      </div>
      <Box
        display="grid"
        gridTemplateColumns={"repeat(3, 1fr)"}
        flex={1}
        gap={2}
      >
        <Box className={s.setItem}>
          <Typography sx={{ fontWeight: 600, fontSize: 20, height: 24 }}>
            {displayValue(set.reps.value)}
          </Typography>
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              className={classes?.unitText}
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              reps
            </Typography>
            {renderDiffBadge(set.reps.diff)}
          </Box>
        </Box>

        <Box className={s.setItem}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Typography sx={{ fontWeight: 600, fontSize: 20, height: 24 }}>
              {displayValue(set.weight.value)}
            </Typography>
            <Typography
              variant="body2"
              className={classes?.unitText}
              sx={{ color: (theme) => theme.palette.text.secondary }}
            >
              {unitSetLabelCoach(units, exerciseTypeSet)}
            </Typography>
            {set.weight.isCompatible
              ? renderDiffBadge(set.weight.diff)
              : set.weight.originalWeight && (
                  <Typography
                    variant="body2"
                    className={classes?.unitText}
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontSize: 14,
                      ml: 0.5,
                    }}
                  >
                    • {set.weight.originalWeight}{" "}
                    {unitSetLabelCoach(
                      set.weight.originalUnits as Units,
                      exerciseTypeSet,
                    )}
                  </Typography>
                )}
          </Box>
        </Box>

        {hasExtraColumn(typeExtraMeasurement) && set.extra && (
          <Box className={s.setItem}>
            <Box display="flex" alignItems="center" gap={0.5}>
              <Typography sx={{ fontWeight: 600, fontSize: 20 }}>
                {displayValue(set.extra.value)}
              </Typography>
              <Box display="flex" alignItems="center">
                <Typography
                  variant="body2"
                  className={classes?.unitText}
                  sx={{ color: (theme) => theme.palette.text.secondary }}
                >
                  {typeExtraMeasurement}
                </Typography>
                {renderDiffBadge(set.extra.diff)}
                {!set.extra.isCompatible && set.extra.originalExtra && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: (theme) => theme.palette.text.secondary,
                      fontSize: 14,
                      ml: 0.5,
                    }}
                  >
                    • {set.extra.originalExtra}{" "}
                    {set.extra.originalExtraType}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default WorkoutHistorySet;
